import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatGreetingMessage, type SuggestedPromptLabel } from 'skipify-types';

import type { AgentSetupConfig } from '../OnboardingDraftContext';
import { ArrowUpIcon } from './icons/ArrowUpIcon';

/** Matches ella-sdk `Widget.tsx` / `ELLA_DEV_TOKENS` typography for the onboarding preview. */
const ELLA_PREVIEW_FONT_FAMILY =
  "'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, system-ui, -apple-system, sans-serif";

const ELLA_PREVIEW_CONTAINER_WIDTH = 336;
const ELLA_PREVIEW_CONTAINER_HEIGHT = 550;

export const ELLA_SDK_DEFAULT_BRANDING = {
  threadBackground: '#FEFEFE',
  brandAccent: '#01EAD3',
  textColor: '#1E1E1E',
} as const;

const ELLA_PREVIEW_COLORS = {
  textPrimary: ELLA_SDK_DEFAULT_BRANDING.textColor,
  textSecondary: '#757575',
  textMuted: '#474747',
  border: '#E0E0E0',
  promptBorder: '#D4D4D4',
  surface: ELLA_SDK_DEFAULT_BRANDING.threadBackground,
} as const;

const ELLA_PREVIEW_TYPOGRAPHY = {
  headerTitle: { fontSize: 12, fontWeight: 600, lineHeight: '140%' },
  openingMessage: { fontSize: 16, fontWeight: 500, lineHeight: 'normal' },
} as const;

type Props = Pick<
  AgentSetupConfig,
  'agentName' | 'avatarSrc' | 'customGreeting' | 'suggestedPrompts'
>;

export const EllaOnboardingPreview = ({
  agentName,
  avatarSrc,
  customGreeting,
  suggestedPrompts,
}: Props): JSX.Element => {
  const prompts = suggestedPrompts.slice(0, 3);
  const displayName = agentName.trim() || 'Ella';
  const openingMessage = formatGreetingMessage(customGreeting, displayName);

  return (
    <Box
      data-testid="ella-onboarding-preview"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: ELLA_PREVIEW_CONTAINER_WIDTH,
        height: ELLA_PREVIEW_CONTAINER_HEIGHT,
        minHeight: ELLA_PREVIEW_CONTAINER_HEIGHT,
        mx: 'auto',
        borderRadius: '12px',
        border: `1px solid ${ELLA_PREVIEW_COLORS.border}`,
        boxShadow: '0 4px 8px -2px rgba(16, 24, 40, 0.1), 0 2px 4px -2px rgba(16, 24, 40, 0.06)',
        overflow: 'hidden',
        bgcolor: ELLA_SDK_DEFAULT_BRANDING.threadBackground,
        color: ELLA_SDK_DEFAULT_BRANDING.textColor,
        fontFamily: ELLA_PREVIEW_FONT_FAMILY,
        '& .MuiTypography-root': {
          fontFamily: 'inherit',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          flexShrink: 0,
          borderBottom: `1px solid ${ELLA_PREVIEW_COLORS.border}`,
        }}
      >
        <Box
          component="img"
          src={avatarSrc}
          alt=""
          data-testid="ella-onboarding-preview-agent-avatar"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
        <Typography
          data-testid="ella-onboarding-preview-agent-name"
          sx={{
            ...ELLA_PREVIEW_TYPOGRAPHY.headerTitle,
            color: 'inherit',
          }}
        >
          {displayName}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: 2,
          py: 2,
        }}
      >
        <Typography
          data-testid="ella-onboarding-preview-opening-message"
          sx={{
            ...ELLA_PREVIEW_TYPOGRAPHY.openingMessage,
            color: 'inherit',
            mb: 2,
          }}
        >
          {openingMessage}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {prompts.map((prompt: SuggestedPromptLabel) => (
            <Box
              key={prompt}
              data-testid="ella-onboarding-preview-prompt"
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: '999px',
                border: `1px solid ${ELLA_PREVIEW_COLORS.promptBorder}`,
                bgcolor: ELLA_PREVIEW_COLORS.surface,
                color: ELLA_SDK_DEFAULT_BRANDING.textColor,
                fontSize: 13,
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              {prompt}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          flexShrink: 0,
          borderTop: `1px solid ${ELLA_PREVIEW_COLORS.border}`,
        }}
      >
        <Box
          sx={{
            flex: 1,
            px: 1.5,
            py: 1,
            borderRadius: '999px',
            border: `1px solid ${ELLA_PREVIEW_COLORS.border}`,
            color: ELLA_PREVIEW_COLORS.textSecondary,
            fontSize: 13,
            lineHeight: '18px',
          }}
        >
          Ask a question...
        </Box>
        <Box
          component="button"
          type="button"
          data-testid="ella-onboarding-preview-send-button"
          sx={{
            width: 36,
            height: 36,
            border: 'none',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: ELLA_SDK_DEFAULT_BRANDING.brandAccent,
            color: ELLA_SDK_DEFAULT_BRANDING.textColor,
            cursor: 'default',
          }}
        >
          <ArrowUpIcon />
        </Box>
      </Box>
    </Box>
  );
};
